/**
 * MeetingModeLauncher — entry point to the Pool Heat Pump Calculator.
 *
 * Track D — Sun Stellar (May 2026, Ananya).
 *
 * Flow:
 *   1. Page loads → fetch the rep's open Deals (filtered by tenant on the
 *      backend already) so they can pick which Lead the meeting is for.
 *   2. Optional "Sizing without a lead" toggle — backend allows null leadId
 *      so an intake at a trade show can still use the calculator; but the
 *      saved quotation will be unbound until reattached later.
 *   3. On confirm → POST /api/v1/pool-heat-pump/meeting-sessions { leadId }
 *      → receive a 15-min meetingSessionToken → store in sessionStorage
 *      and route to /sales/meeting/calculators (the Calculator Chooser page,
 *      Track D-CHOOSER, May 2026). The chooser then routes to the actual
 *      calculator page (Pool, future SWH, etc.) per rep selection.
 *
 * Direct URL access (e.g. someone who bookmarks /sales/meeting/calculator)
 * is handled server-side: every calculator endpoint 403s without a valid
 * session token; the calculator page itself shows a friendly back-link
 * if the sessionStorage entry is missing or expired.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calculator, Loader2, Search, Target, ArrowRight, AlertTriangle } from 'lucide-react';
import { api } from '@/utils/api';

interface DealRow {
  id: string;
  /**
   * 'deal'    — row originated from /deals (canonical pipeline opportunity).
   * 'contact' — row originated from /contacts (lead with no deal yet; the
   *             backend resolver mints/binds a deal at meeting-session issue
   *             time, or leaves the session unbound if no account exists).
   * Defaults to 'deal' when undefined so older callers keep working.
   */
  kind?: 'deal' | 'contact';
  title: string;
  value?: number;
  account?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName: string } | null;
}

const SESSION_KEY = 'pool-calc.meeting-session';

interface StoredSession {
  token: string;
  sessionId: string;
  expiresAt: string; // ISO
  leadId: string | null;
  leadLabel: string | null;
}

export function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.token || !parsed.expiresAt) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Persist a freshly-issued meeting session (after a mid-meeting 403 refresh).
 * Used by the calculator pages' `onRefreshSession` callback so the next
 * /calculate or /quotations request picks up the new token transparently.
 */
export function writeStoredSession(stored: StoredSession) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
  } catch {
    /* ignore */
  }
}

export function MeetingModeLauncher() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetLeadId = params.get('leadId');

  const [deals, setDeals] = useState<DealRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(presetLeadId);
  const [allowFreeFloating, setAllowFreeFloating] = useState(false);
  const [issuing, setIssuing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Bug 2026-05-15 — admin (and every other role) saw "no leads match"
        // on tenants whose pipeline had Contacts/LeadImports but zero open
        // Deals. The launcher used to fetch ONLY `/deals`, but the backend
        // resolver `resolveLeadIdToDealId` (meeting-session.service.ts:58)
        // already accepts dealId, leadImportId, OR contactId — it derives
        // the deal automatically. So the launcher should surface the
        // broader pool: leads/contacts that the rep can size for, even if
        // no Deal row exists yet.
        //
        // Fetch BOTH `/deals` and `/contacts` in parallel and merge into a
        // single picker. Display a small "Deal" / "Lead" badge so the rep
        // knows which is which. Selecting a contact still works because
        // the backend resolves it to a deal (or accepts an unbound session
        // when the contact isn't on an account-with-deal).
        const [dealsRes, contactsRes] = await Promise.all([
          api.get<any>('/deals').catch(() => ({ data: null })),
          api.get<any>('/contacts').catch(() => ({ data: null })),
        ]);

        const extractArray = (raw: any): any[] => {
          const candidates = [
            raw,
            raw?.data,
            raw?.data?.deals,
            raw?.data?.contacts,
            raw?.data?.items,
            raw?.data?.rows,
            raw?.data?.results,
            raw?.deals,
            raw?.contacts,
            raw?.items,
            raw?.rows,
            raw?.results,
          ];
          for (const c of candidates) if (Array.isArray(c)) return c;
          return [];
        };

        const dealRows: DealRow[] = extractArray(dealsRes.data).map(
          (d: any) => ({
            id: d.id,
            kind: 'deal' as const,
            title: d.title ?? d.name ?? 'Untitled deal',
            value: d.value,
            account: d.account ?? null,
            contact: d.contact ?? null,
          }),
        );

        // Map contacts → DealRow shape so the existing render code works
        // unchanged. `kind: 'contact'` differentiates the badge below.
        const contactRows: DealRow[] = extractArray(contactsRes.data).map(
          (c: any) => {
            const fullName = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ||
              c.displayName || c.name || 'Unnamed contact';
            return {
              id: c.id,
              kind: 'contact' as const,
              title: fullName,
              value: undefined,
              account: c.account ?? (c.accountName ? { id: c.accountId ?? '', name: c.accountName } : null),
              contact: { id: c.id, firstName: c.firstName ?? '', lastName: c.lastName ?? '' },
            };
          },
        );

        // Dedupe: a contact already represented via a deal's `contact.id`
        // would otherwise show twice. Prefer the deal row when both exist.
        const dealContactIds = new Set(
          dealRows.map((d) => d.contact?.id).filter(Boolean) as string[],
        );
        const dedupedContacts = contactRows.filter(
          (c) => !dealContactIds.has(c.id),
        );

        const merged = [...dealRows, ...dedupedContacts];
        if (!cancelled) setDeals(merged);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || 'Could not load leads.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.account?.name?.toLowerCase().includes(q) ||
        `${d.contact?.firstName ?? ''} ${d.contact?.lastName ?? ''}`.toLowerCase().includes(q)
    );
  }, [deals, search]);

  async function startMeeting() {
    setIssuing(true);
    setError(null);
    try {
      const leadId = allowFreeFloating ? null : selected;
      if (!allowFreeFloating && !leadId) {
        setError('Pick a lead first, or tick "Sizing without a lead" to proceed unbound.');
        setIssuing(false);
        return;
      }

      const res = await api.post<{
        data: { meetingSessionToken: string; sessionId: string; expiresAt: string; leadId: string | null };
      }>('/pool-heat-pump/meeting-sessions', { leadId });

      const { meetingSessionToken, sessionId, expiresAt } = res.data.data;
      const lead = leadId ? deals.find((d) => d.id === leadId) : null;
      const stored: StoredSession = {
        token: meetingSessionToken,
        sessionId,
        expiresAt,
        leadId,
        leadLabel: lead
          ? `${lead.title}${lead.account?.name ? ` · ${lead.account.name}` : ''}`
          : null,
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
      // Track D-CHOOSER (May 2026) — show the Calculator Chooser page first
      // instead of jumping straight to the pool calculator. The chooser lists
      // all calculators enabled for the current tenant; rep picks one.
      navigate('/sales/meeting/calculators');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not start a meeting session.');
    } finally {
      setIssuing(false);
    }
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <Calculator size={28} style={{ color: '#0066b3' }} />
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>Initiate Meeting Mode</h1>
      </div>
      <p style={{ color: '#666', marginTop: 4 }}>
        Meeting Mode calculators are tools for live sizing during sales conversations
        — not free utilities. Pick the lead you're sizing for. The 15-minute session
        locks the calculator(s) to this lead so resulting quotations auto-attach to
        their record.
      </p>

      <div style={{ marginTop: 18, position: 'relative' }}>
        <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: '#888' }} />
        <input
          type="text"
          placeholder="Search leads — title, account, or contact..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px 8px 32px',
            border: '1px solid #ddd',
            borderRadius: 6,
            fontSize: 14,
          }}
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Loader2 className="animate-spin" />
        </div>
      ) : error ? (
        <div style={{ padding: 12, background: '#fde7e7', color: '#8b2222', borderRadius: 6, marginTop: 12, display: 'flex', gap: 8 }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ marginTop: 16, padding: 24, textAlign: 'center', color: '#666', border: '1px dashed #ddd', borderRadius: 6 }}>
          No leads match.{' '}
          <button
            type="button"
            onClick={() => setAllowFreeFloating(true)}
            style={{ color: '#0066b3', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer' }}
          >
            Proceed without a lead?
          </button>
        </div>
      ) : (
        <div style={{ marginTop: 16, maxHeight: 360, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
          {filtered.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => {
                setAllowFreeFloating(false);
                setSelected(d.id);
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '10px 14px',
                background: selected === d.id ? '#f3f8ff' : 'white',
                border: 0,
                borderBottom: '1px solid #eee',
                cursor: 'pointer',
                display: 'flex',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <Target size={16} style={{ color: selected === d.id ? '#0066b3' : '#999' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{d.title}</span>
                  {/* Bug 2026-05-15: surface whether the row is a Deal or a
                      pre-deal Contact/Lead so the rep doesn't confuse the
                      two when the launcher pulls from both pools. */}
                  {d.kind === 'contact' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: '#fdf2e7',
                        color: '#a05a1e',
                        border: '1px solid #f3d6b2',
                      }}
                    >
                      LEAD
                    </span>
                  )}
                  {d.kind === 'deal' && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: 0.4,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: '#e8f0ff',
                        color: '#0066b3',
                        border: '1px solid #c8d8f0',
                      }}
                    >
                      DEAL
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {d.account?.name ?? '(no account)'}
                  {d.contact ? ` · ${d.contact.firstName} ${d.contact.lastName}` : ''}
                </div>
              </div>
              {selected === d.id && <span style={{ color: '#0066b3', fontWeight: 600, fontSize: 12 }}>SELECTED</span>}
            </button>
          ))}
        </div>
      )}

      <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, fontSize: 13, color: '#666' }}>
        <input
          type="checkbox"
          checked={allowFreeFloating}
          onChange={(e) => {
            setAllowFreeFloating(e.target.checked);
            if (e.target.checked) setSelected(null);
          }}
        />
        Sizing without a lead (e.g. trade-show walk-up). The quotation can be re-attached later.
      </label>

      <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          disabled={issuing}
          style={{
            padding: '8px 16px',
            border: '1px solid #ccc',
            background: 'white',
            borderRadius: 6,
            cursor: issuing ? 'not-allowed' : 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={startMeeting}
          disabled={issuing || (!allowFreeFloating && !selected)}
          style={{
            padding: '8px 18px',
            background: '#0066b3',
            color: 'white',
            border: 0,
            borderRadius: 6,
            cursor: issuing ? 'wait' : 'pointer',
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            opacity: !allowFreeFloating && !selected ? 0.5 : 1,
          }}
        >
          {issuing ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Choose calculator
        </button>
      </div>
    </div>
  );
}
